// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useImperativeHandle, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useChainDetailQuery, useEditChainMutation } from "@/api/hooks/use-chains";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { TIMEZONE_OPTIONS } from "@/lib/timezones";
import { cn } from "@/lib/utils";

const chainEditSchema = z.object({
  chainName: z
    .string()
    .min(3, "Must be 3-32 characters")
    .max(32, "Must be 3-32 characters")
    .regex(/^[a-z0-9_]+$/, "Only lowercase, numbers, and underscores"),
  legibleName: z.string().trim().min(3, "Must be 3-32 characters").max(32, "Must be 3-32 characters"),
  defaultTimeZone: z.string().min(1, "Required"),
});

type ChainEditValues = z.infer<typeof chainEditSchema>;

export interface ChainEditTabHandle {
  reset: () => void;
}

interface ChainEditTabProps {
  chainName: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<ChainEditTabHandle>;
}

export function ChainEditTab({ chainName, onDirtyChange, ref }: ChainEditTabProps) {
  const { t } = useTranslation("back-office");
  const { data: chain } = useChainDetailQuery(chainName);
  const editChain = useEditChainMutation(chainName);

  const form = useForm<ChainEditValues>({
    resolver: zodResolver(chainEditSchema),
    defaultValues: {
      chainName: chain?.chainName ?? "",
      legibleName: chain?.legibleName ?? "",
      defaultTimeZone: chain?.defaultTimeZone ?? "",
    },
  });

  useEffect(
    function resetFormWhenChainDataLoads() {
      if (chain) {
        form.reset({
          chainName: chain.chainName,
          legibleName: chain.legibleName,
          defaultTimeZone: chain.defaultTimeZone,
        });
      }
    },
    [chain, form],
  );

  async function onSubmit(values: ChainEditValues) {
    try {
      await editChain.mutateAsync(values);
      toast.success(t("chains.notifications.updated", { name: values.legibleName }));
      form.reset(values);
    } catch {
      // Error toast handled by API middleware
    }
  }

  const { isDirty, dirtyFields } = form.formState;

  useImperativeHandle(ref, () => ({ reset: () => form.reset() }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="legibleName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("chains.form.legibleName")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className={cn(dirtyFields.legibleName && "bg-sidebar-accent-hover transition-colors")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="chainName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("chains.form.chainName")}</FormLabel>
                <FormControl>
                  <Input {...field} disabled className="font-mono" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultTimeZone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("chains.form.defaultTimeZone")}</FormLabel>
                <FormControl>
                  <VirtualSelectPopover
                    value={field.value}
                    onChange={field.onChange}
                    options={TIMEZONE_OPTIONS}
                    placeholder={t("chains.form.selectTimezone")}
                    searchPlaceholder={t("chains.form.searchTimezones")}
                    emptyText={t("chains.form.noTimezones")}
                    className={cn(dirtyFields.defaultTimeZone && "bg-sidebar-accent-hover transition-colors")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isDirty && <SaveDiscardPane onDiscard={() => form.reset()} saving={editChain.isPending} />}
        </form>
      </Form>
    </div>
  );
}
